import { React, useState, useEffect, useRef } from 'react';
import { Flex, Box, Button, useToast, Divider } from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { useDispatch, useTrackedState } from '../../config/store';

import StyledAccordionItem from './StyledAccordionItem';
import GreyTextBox from './GreyTextBox';

function JobOpeningAccordion({ interview }) {
  const state = useTrackedState();
  const [fetchingPDF, setFetchingPDF] = useState(false);
  const dispatch = useDispatch();
  let fetchingPDFTimeout;

  const toast = useToast();
  const displayingToast = useRef(false); // useRef to prevent multiple toasts from being displayed due to useEffect

  const fetchPDF = () => {
    if (state.jdPDF?.id === interview.interviewData.id) {
      window.open(window.URL.createObjectURL(state.jdPDF.url));
      window.URL.revokeObjectURL(state.jdPDF.url);
    } else {
      dispatch({
        type: 'GET_JD_PDF',
        interviewId: interview.interviewData.id,
        token: state.user.token,
      });
      setFetchingPDF(true);
      fetchingPDFTimeout = setTimeout(() => setFetchingPDF(false), 10000);
    }
  };

  useEffect(() => {
    if (fetchingPDF && state.jdPDF?.id === interview.interviewData.id) {
      window.open(window.URL.createObjectURL(state.jdPDF.url));
      setFetchingPDF(false);
      clearTimeout(fetchingPDFTimeout);
      window.URL.revokeObjectURL(state.jdPDF.url);
    }

    if (fetchingPDF && state.error?.message === 'PDF Not Found') {
      // First check if toast is already displaying
      if (displayingToast.current) displayingToast.current = false;
      else {
        toast({
          position: 'bottom-right',
          isClosable: true,
          title: 'PDF not found for this Job Opening',
          description: `Contact us at screeners-feedback@nextonlabs.com so that we can check it!`,
          status: 'warning',
          duration: null,
          variant: 'left-accent',
        });
        dispatch({ type: 'CLEAN_ERROR' });
        displayingToast.current = true;
      }
    }
  }, [state.jdPDF, fetchingPDF, state.error]);

  return (
    <StyledAccordionItem title='Job Opening Information'>
      <Box mb='24px' minHeight='24px'>
        <Button
          variant='link'
          size='sm'
          color='blue.500'
          letterSpacing='-0.2px'
          onClick={fetchPDF}
          rightIcon={<ExternalLinkIcon boxSize={5} mb='3px' />}
          isLoading={fetchingPDF && state.pending}
          loadingText='Full Job Description (PDF)'
          spinnerPlacement='end'
          mt={fetchingPDF && state.pending ? '3px' : ''}>
          Full Job Description (PDF)
        </Button>
      </Box>

      <Flex gap='12'>
        <Flex flexDirection='column' flex='2'>
          <GreyTextBox title='Client:' text={interview.candidateData.Snapshot_Client} />
          <GreyTextBox title='Job Title:' text={interview.candidateData.Snapshot_Search_Name} />
          <GreyTextBox title='Seniority:' text={interview.candidateData.Snapshot_Seniority?.join(', ')} />
          <GreyTextBox title='Job Type:' text={interview.candidateData.Snapshot_Job_Type} />
          <GreyTextBox title='Contract:' text={interview.candidateData.Snapshot_Contract_Type} />
        </Flex>
        <Flex flexDirection='column' flex='3'>
          <GreyTextBox title='Position Location:' text={interview.candidateData.Snapshot_Location} />
          <GreyTextBox title='Remote Policy:' text={interview.candidateData.Snapshot_Remote_Policy} />
          <GreyTextBox title='Role Stack:' text={interview.candidateData.Snapshot_Role_Stack?.join(', ')} />
          <GreyTextBox title='Company Industry:' text={interview.candidateData.Snapshot_Industry} />
        </Flex>
      </Flex>

      {interview.candidateData.Snapshot_Notes_On_Stack && (
        <>
          <Divider my='24px' />
          <Flex gap='12'>
            <Flex flexDirection='column' flex='2'>
              <GreyTextBox title='Job Opening Instructions:' text={interview.candidateData.Snapshot_Notes_On_Stack} />
            </Flex>
            <Flex display='hidden' flexDirection='column' flex='1' />
          </Flex>
        </>
      )}
    </StyledAccordionItem>
  );
}

export default JobOpeningAccordion;
